import { ReactElement, useContext } from 'react';

import FaveHeart from '@/components/app/contentPlayer/FaveHeart';
import { AuthModalContext } from '@/context/AuthModalContextProvider';
import { useGuideDuration } from '@/hooks/app/useGuideDuration';
import { useGuideInfo } from '@/hooks/app/useGuideInfo';
import { useGuidePlay } from '@/hooks/app/useGuidePlay';
import { useUserState } from '@/hooks/store';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import circleCheck from 'icons/circle-check.svg';
import positionIndicator from 'icons/position-indicator.svg';

import { useMediaPlayerContext } from '../../contentPlayer/MediaPlayerProvider';
import {
	ClickableWrapper,
	Description,
	Duration,
	FaveHeartContainer,
	GuidePosition,
	LockIcon,
	Title,
	Wrapper,
} from './styles';
import { GuideRowProps } from './types';

const GuideRow = ({ guide, program, programProgress }: GuideRowProps): ReactElement => {
	const onGuidePlay = useGuidePlay();
	const user = useUserState();
	const routerPush = useRouterPush();
	const { setIsModalOpen } = useContext(AuthModalContext);
	const { guideState, title, description, isCourse, isAvailable, isDisabled } = useGuideInfo(
		guide,
		program,
		programProgress,
	);
	const { hideControls } = useMediaPlayerContext();

	const duration = useGuideDuration(guide?.asset?.duration);

	const onClick = async () => {
		if (!user) {
			setIsModalOpen(true);
			return;
		}

		if (program?.meditation_type === 'timer') {
			await routerPush(`/app/player/${guide.id}`);
		} else {
			await onGuidePlay(guide.id, program);
		}
	};

	return (
		<Wrapper isAvailable={isAvailable}>
			<ClickableWrapper isDisabled={hideControls} onClick={onClick} data-testid="guide-row-clickable-wrapper">
				<Title isCourse={isCourse}>
					{(guideState === 'course_completed' || guideState === 'course_unlocked') && (
						<GuidePosition
							$isCompleted={guideState === 'course_completed'}
							as={guideState === 'course_completed' ? circleCheck : positionIndicator}
						/>
					)}
					{title}
				</Title>
				{description && <Description>{description}</Description>}
				{duration && <Duration>{duration}</Duration>}
			</ClickableWrapper>
			{!hideControls && (
				<FaveHeartContainer>
					{isAvailable && <FaveHeart guideId={guide.id} guideTitle={guide.title} />}
				</FaveHeartContainer>
			)}
			{!isAvailable && !isDisabled && <LockIcon />}
		</Wrapper>
	);
};

export default GuideRow;
